/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Stack,
  Avatar,
  Tooltip,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  IconButton,
  Typography,
} from '@mui/material';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';

export default function UsersTableBody({
  activeTab,
  currentPageData,
  handleClick,
  handleClickRow,
  selected,
  handleOpenMenu,
  departmentsData,
  hasAnyAction,
}) {
  const { t } = useTranslation();
  const formatRoleDisplay = (role) => {
    if (!role) return '—';
    const translated = t(`roles.${role}`);
    if (translated === `roles.${role}` || translated.startsWith('roles.')) {
      return role
        .replace('ROLE_', '')
        .replace(/_/g, ' ')
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }
    return translated;
  };

  return (
    <TableBody>
      {currentPageData.map((row) => {
        const {
          fullName,
          role,
          email,
          phoneNumber,
          status,
          avatarUrl,
          id,
          departmentNames = [],
        } = row;
        const isItemSelected = selected.indexOf(email) !== -1;

        return (
          <TableRow
            hover
            key={id}
            tabIndex={-1}
            role="checkbox"
            selected={isItemSelected}
            onClick={() => handleClickRow(row)}
            sx={{ cursor: 'pointer' }}
          >
            {activeTab === 0 && (
              <TableCell padding="checkbox">
                <Checkbox
                  checked={isItemSelected}
                  onChange={(event) => handleClick(event, email, id)}
                  onClick={(e) => e.stopPropagation()}
                />
              </TableCell>
            )}
            <TableCell component="th" scope="row">
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar src={avatarUrl} sx={{ width: 40, height: 40 }} />
                <Typography variant="subtitle2" noWrap>
                  {fullName}
                </Typography>
              </Stack>
            </TableCell>
            <TableCell align="left">{email}</TableCell>
            <TableCell align="left">
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography variant="body2">
                  {departmentNames.slice(0, 2).join(', ')}
                </Typography>
                {departmentNames.length > 2 && (
                  <Tooltip title={departmentNames.join(', ')} arrow>
                    <Box
                      sx={{
                        bgcolor: 'rgba(145, 158, 171, 0.12)',
                        borderRadius: '4px',
                        px: 0.5,
                      }}
                    >
                      <Iconify icon="eva:more-horizontal-fill" width={16} />
                    </Box>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
            <TableCell align="left" sx={{ fontWeight: 600 }}>
              {formatRoleDisplay(role)}
            </TableCell>
            <TableCell align="left">{phoneNumber || '—'}</TableCell>
            <TableCell align="left">
              <Label color={String(status) === 'false' ? 'error' : 'success'}>
                {String(status) === 'true'
                  ? t('userStatus.active')
                  : t('userStatus.inactive')}
              </Label>
            </TableCell>

            {hasAnyAction && (
              <TableCell
                align="center"
                sx={{
                  position: 'sticky',
                  right: 0,
                  backgroundColor: 'background.paper',
                }}
              >
                <IconButton onClick={(e) => handleOpenMenu(e, row)}>
                  <Iconify icon="eva:more-vertical-fill" />
                </IconButton>
              </TableCell>
            )}
          </TableRow>
        );
      })}
    </TableBody>
  );
}

UsersTableBody.propTypes = {
  currentPageData: PropTypes.array,
  handleClick: PropTypes.func,
  selected: PropTypes.array,
  handleOpenMenu: PropTypes.func,
  handleClickRow: PropTypes.func,
  activeTab: PropTypes.number,
  departmentsData: PropTypes.array,
  hasAnyAction: PropTypes.bool.isRequired,
};
